/**
 * Richieste Materiali — ordini di materiali inviati al fornitore.
 * Solo per admin / impiegato amministrativo / responsabile cantiere.
 */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api";
import { useAuth, isAdmin } from "@/context/AuthContext";
import ScansioneInventario from "@/pages/magazzino/ScansioneInventario";
import useFormDraft from "@/hooks/useFormDraft";
import {
  Plus, FileDown, Mail, Package, X, Trash2, Send,
  CheckCircle2, AlertTriangle, Clock, PackageCheck, RefreshCw,
  MessageCircle, Sparkles, Warehouse,
} from "lucide-react";
import { AccettaRichiestaModal, RifiutaRichiestaButton } from "./richieste/AccettaRifiuta";
import StoricoRichiestaModal from "./richieste/StoricoRichiestaModal";
import RicalcolaScadenzeButton from "./richieste/RicalcolaScadenzeButton";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATO_MAP = {
  bozza:     { label: "Bozza",     cls: "bg-amber-100 text-amber-800 border-amber-300",     icon: Clock },
  inviata:   { label: "Inviata",   cls: "bg-blue-100 text-blue-800 border-blue-200",     icon: Send },
  in_attesa: { label: "In attesa", cls: "bg-slate-100 text-slate-700 border-slate-300", icon: Clock },
  accettata: { label: "Accettata", cls: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: CheckCircle2 },
  rifiutata: { label: "Rifiutata", cls: "bg-rose-100 text-rose-800 border-rose-300",   icon: X },
  parziale:  { label: "Parziale",  cls: "bg-amber-100 text-amber-800 border-amber-200",  icon: Clock },
  ritirata:  { label: "Ritirata",  cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: PackageCheck },
  annullata: { label: "Annullata", cls: "bg-slate-100 text-slate-600 border-slate-200",   icon: X },
};
export { STATO_MAP };

const COND_PAGAMENTO = [
  { key: "immediato",    label: "Immediato" },
  { key: "30",           label: "30 giorni" },
  { key: "60",           label: "60 giorni" },
  { key: "90",           label: "90 giorni" },
  { key: "30_60",        label: "30 / 60 (50-50)" },
  { key: "30_60_90",     label: "30 / 60 / 90 (rate uguali)" },
  { key: "personalizzato", label: "Personalizzato" },
];
export { COND_PAGAMENTO };

const UM_OPTIONS = ["pz", "mq", "mc", "ml", "kg", "q", "t", "lt", "cf", "sacco", "bancale", "ora"];

export default function RichiesteMateriali() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [accettaFor, setAccettaFor] = useState(null);
  const [storicoFor, setStoricoFor] = useState(null);
  const [prezziFor, setPrezziFor] = useState(null);
  const { user } = useAuth();
  const admin = isAdmin(user);

  const { data: richieste = [], isLoading } = useQuery({
    queryKey: ["richieste-materiali"],
    queryFn: () => api.get("/richieste-materiali").then((r) => r.data),
  });

  const { data: cantieri = [] } = useQuery({
    queryKey: ["cantieri-all"],
    queryFn: () => api.get("/cantieri", { params: { tipo: "all" } }).then((r) => r.data),
  });

  const { data: fornitori = [] } = useQuery({
    queryKey: ["fornitori-anagrafica"],
    queryFn: () => api.get("/entita/fornitori").then((r) => r.data),
  });

  const create = useMutation({
    mutationFn: (payload) => api.post("/richieste-materiali", payload).then((r) => r.data),
    onSuccess: async (data) => {
      qc.invalidateQueries({ queryKey: ["richieste-materiali"] });
      if (data.email_inviata) {
        toast.success(`${data.numero_richiesta} — email inviata al fornitore`);
      } else if (data.fornitore_email) {
        toast.warning(`${data.numero_richiesta} creata, ma email non inviata: ${data.email_error || "SMTP non configurato"}`);
      } else {
        toast.success(`${data.numero_richiesta} creata (nessuna email — fornitore senza indirizzo)`);
      }
      setOpen(false);
      // Auto-invio WhatsApp se il fornitore ha un numero di telefono
      if (data.fornitore_telefono) {
        toast.info("Apro WhatsApp per il fornitore…", { duration: 2500 });
        await new Promise((r) => setTimeout(r, 600));
        whatsappShare(data);
      }
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore creazione richiesta"),
  });

  const cambiaStato = useMutation({
    mutationFn: ({ id, stato, note }) =>
      api.patch(`/richieste-materiali/${id}/stato`, { stato, note: note || "" }).then((r) => r.data),
    onSuccess: () => {
      toast.success("Stato aggiornato");
      qc.invalidateQueries({ queryKey: ["richieste-materiali"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const reinviaEmail = useMutation({
    mutationFn: (id) => api.post(`/richieste-materiali/${id}/reinvia-email`).then((r) => r.data),
    onSuccess: (data) => {
      if (data.ok) toast.success("Email reinviata al fornitore");
      else toast.error(`Invio fallito: ${data.result?.error || "SMTP non configurato"}`);
      qc.invalidateQueries({ queryKey: ["richieste-materiali"] });
    },
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/richieste-materiali/${id}`),
    onSuccess: () => {
      toast.success("Richiesta eliminata");
      qc.invalidateQueries({ queryKey: ["richieste-materiali"] });
      qc.invalidateQueries({ queryKey: ["magazzino"] });
      qc.invalidateQueries({ queryKey: ["magazzino-bozze"] });
    },
    onError: (e) => {
      const status = e?.response?.status ? `HTTP ${e.response.status} · ` : "";
      toast.error(`Eliminazione fallita — ${status}${e?.response?.data?.detail || e.message || "Errore"}`);
    },
  });

  const whatsappShare = async (r) => {
    try {
      const res = await api.post(`/richieste-materiali/${r.richiesta_id}/share`);
      const relativePath = res.data.path || res.data.share_url;
      const publicUrl = res.data.share_url && res.data.share_url.startsWith("http")
        ? res.data.share_url
        : `${REACT_APP_BACKEND_URL}${relativePath}`;
      const righeSummary = (r.materiali || [])
        .slice(0, 5)
        .map((m) => {
          const pu = Number(m.prezzo_unitario) || 0;
          const tot = Number(m.totale_riga) || ((Number(m.quantita) || 0) * pu);
          const codice = m.codice_articolo ? `Cod. ${m.codice_articolo} — ` : "";
          const prezzo = pu > 0 ? ` — € ${pu.toFixed(2)}/${m.unita_misura} — Tot. € ${tot.toFixed(2)}` : "";
          return `• ${codice}${m.descrizione} — ${m.quantita} ${m.unita_misura}${prezzo}`;
        })
        .join("\n");
      const extra = (r.materiali || []).length > 5 ? `\n…e altre ${(r.materiali || []).length - 5}` : "";
      const message = [
        `Buongiorno, richiesta materiali *${r.numero_richiesta}*`,
        `Cantiere: ${r.cantiere_nome}`,
        r.data_consegna_richiesta ? `Consegna entro: ${r.data_consegna_richiesta}` : "",
        "",
        righeSummary + extra,
        "",
        `Totale ordine: € ${(r.materiali || []).reduce((somma, m) => {
          const pu = Number(m.prezzo_unitario) || 0;
          const tot = Number(m.totale_riga) || ((Number(m.quantita) || 0) * pu);
          return somma + tot;
        }, 0).toFixed(2)}`,
        "",
        `PDF ordine: ${publicUrl}`,
        "",
        "Alla consegna riportare sulla bolla questo numero. Grazie!",
      ].filter(Boolean).join("\n");
      const phone = (r.fornitore_telefono || "").replace(/[^\d+]/g, "");
      const url = phone
        ? `https://wa.me/${phone.replace(/^\+/, "")}?text=${encodeURIComponent(message)}`
        : `https://wa.me/?text=${encodeURIComponent(message)}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore generazione link WhatsApp");
    }
  };

  const [scannerFor, setScannerFor] = useState(null);

  const totRighe = richieste.reduce((s, r) => s + (r.materiali?.length || 0), 0);

  return (
    <div className="space-y-6" data-testid="page-richieste-materiali">
      <div className="bg-white border border-slate-200">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="overline">Ordini a fornitore</div>
            <h3 className="font-display font-bold flex items-center gap-2">
              <Package className="w-4 h-4 text-slate-500" />
              {richieste.length} richieste · {totRighe} righe totali
            </h3>
            <p className="text-[11px] text-slate-500 mt-1">
              Ordini materiali inviati direttamente al fornitore. Alla creazione parte l'email con PDF
              allegato; quando arriva la bolla la richiesta diventa <b>ritirata</b>.
            </p>
          </div>
          <button
            onClick={() => setOpen(true)}
            data-testid="btn-new-richiesta-materiali"
            className="inline-flex items-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-sm shadow-brutal"
          >
            <Plus className="w-4 h-4" /> Nuova richiesta materiali
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-widest">
              <tr>
                <th className="text-left p-3">N°</th>
                <th className="text-left p-3">Data</th>
                <th className="text-left p-3">Cantiere</th>
                <th className="text-left p-3">Fornitore</th>
                <th className="text-right p-3">Righe</th>
                <th className="text-left p-3">Stato</th>
                <th className="text-left p-3">Email</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={8} className="p-6 text-center text-slate-400 italic">Caricamento…</td></tr>
              )}
              {!isLoading && richieste.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-slate-400 text-sm">
                    Nessuna richiesta materiali.<br />
                    <span className="text-[11px]">Clicca <b>Nuova richiesta materiali</b> per creare il primo ordine al fornitore.</span>
                  </td>
                </tr>
              )}
              {richieste.map((r) => {
              const S = STATO_MAP[r.stato] || STATO_MAP.inviata;
              const emailKo = r.stato === "inviata" && !r.email_inviata && r.fornitore_email;
              const emailNoAddr = !r.fornitore_email;
              return (
                <tr key={r.richiesta_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`row-rm-${r.numero_richiesta}`}>
                  <td className="p-3 font-mono font-bold text-orange-700">{r.numero_richiesta}</td>
                  <td className="p-3 text-xs">{r.data_richiesta}</td>
                  <td className="p-3">{r.cantiere_nome}</td>
                  <td className="p-3">
                    <div>{r.fornitore_nome}</div>
                    {r.fornitore_email && <div className="text-[11px] text-slate-500 font-mono">{r.fornitore_email}</div>}
                  </td>
                  <td className="p-3 text-right font-mono">{(r.materiali || []).length}</td>
                  <td className="p-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 border rounded-sm text-[11px] font-semibold ${S.cls}`}>
                      <S.icon className="w-3 h-3" /> {S.label}
                    </span>
                  </td>
                  <td className="p-3 text-xs">
                    {r.email_inviata && (
                      <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3 h-3" /> Inviata</span>
                    )}
                    {emailKo && (
                      <span className="inline-flex items-center gap-1 text-amber-700" title={r.email_error}>
                        <AlertTriangle className="w-3 h-3" /> Fallita
                      </span>
                    )}
                    {emailNoAddr && (
                      <span className="text-slate-400">senza email</span>
                    )}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-1">
                      {(r.stato === "inviata" || r.stato === "in_attesa" || r.stato === "bozza") && (
                        <>
                          <button
                            onClick={() => setAccettaFor(r)}
                            data-testid={`btn-accetta-${r.numero_richiesta}`}
                            title="Accetta richiesta e genera rate in Contabilità"
                            className="p-1 text-emerald-700 hover:bg-emerald-50 border border-emerald-300 rounded-sm inline-flex items-center gap-1"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            <span className="text-[11px] font-semibold">Accetta</span>
                          </button>
                          <RifiutaRichiestaButton richiesta={r} onDone={() => qc.invalidateQueries({ queryKey: ["richieste-materiali"] })} />
                        </>
                      )}
                      {r.stato === "accettata" && (r.rate_pagamento || []).length > 0 && (
                        <>
                          <span
                            title={`Rate generate: ${(r.rate_pagamento || []).map(p => `${p.numero}/${p.totale_rate}: €${p.importo.toFixed(2)} → ${p.data_scadenza}`).join('\n')}`}
                            className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-300 px-1.5 py-0.5 rounded-sm"
                            data-testid={`badge-rate-${r.numero_richiesta}`}
                          >
                            {(r.rate_pagamento || []).length} rate
                          </span>
                          <RicalcolaScadenzeButton
                            richiesta={r}
                            onDone={() => qc.invalidateQueries({ queryKey: ["richieste-materiali"] })}
                          />
                        </>
                      )}
                      {!["bozza", "annullata", "rifiutata", "ritirata"].includes(r.stato) && (
                        <button
                          type="button"
                          onClick={() => setScannerFor(r)}
                          title={`Scansiona materiali in uscita per ${r.numero_richiesta}`}
                          data-testid={`btn-scan-uscita-${r.numero_richiesta}`}
                          className="px-2 py-1 text-amber-800 hover:bg-amber-50 border border-amber-300 rounded-sm inline-flex items-center gap-1"
                        >
                          <Package className="w-3.5 h-3.5" />
                          <span className="text-[11px] font-semibold">
                            Scansiona uscita
                          </span>
                        </button>
                      )}

                      {admin && (
                        <button
                          type="button"
                          onClick={() => setPrezziFor(r)}
                          className="px-2 py-1 text-xs font-semibold text-blue-700 border border-blue-300 rounded hover:bg-blue-50"
                        >
                          Dettaglio prezzi
                        </button>
                      )}
                      <a
                        href={`${REACT_APP_BACKEND_URL}/api/richieste-materiali/${r.richiesta_id}/pdf`}
                        target="_blank" rel="noreferrer"
                        title="Scarica PDF"
                        data-testid={`btn-pdf-${r.numero_richiesta}`}
                        className="p-1 text-slate-700 hover:bg-slate-100 rounded-sm"
                      >
                        <FileDown className="w-4 h-4" />
                      </a>
                      <button
                        onClick={() => setStoricoFor(r)}
                        title="Timeline / storico stati e rate"
                        data-testid={`btn-storico-${r.numero_richiesta}`}
                        className="p-1 text-slate-700 hover:bg-slate-100 rounded-sm"
                      >
                        <Clock className="w-4 h-4" />
                      </button>
                      {r.fornitore_email && r.stato !== "ritirata" && r.stato !== "annullata" && (
                        <button
                          onClick={() => reinviaEmail.mutate(r.richiesta_id)}
                          title="Reinvia email al fornitore"
                          data-testid={`btn-resend-${r.numero_richiesta}`}
                          className="p-1 text-blue-700 hover:bg-blue-50 rounded-sm"
                          disabled={reinviaEmail.isPending}
                        >
                          {reinviaEmail.isPending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                        </button>
                      )}
                      {r.stato !== "annullata" && (
                        <button
                          onClick={() => whatsappShare(r)}
                          title={r.fornitore_telefono
                            ? `Invia su WhatsApp a ${r.fornitore_telefono}`
                            : "Apri WhatsApp Web (scegli il contatto)"}
                          data-testid={`btn-whatsapp-${r.numero_richiesta}`}
                          className="p-1 text-emerald-700 hover:bg-emerald-50 rounded-sm"
                        >
                          <MessageCircle className="w-4 h-4" />
                        </button>
                      )}
                      <StatoSelect
                        richiesta={r}
                        onChange={(stato) => cambiaStato.mutate({ id: r.richiesta_id, stato })}
                      />
                      <button
                        onClick={() => {
                          const fornitore = r.fornitore || "senza fornitore";
                          const nRighe = (r.materiali || []).length;
                          if (window.confirm(
                            `Eliminare definitivamente la richiesta ${r.numero_richiesta}?\n\n` +
                            `Fornitore: ${fornitore}\n` +
                            `Materiali: ${nRighe} riga${nRighe === 1 ? "" : "he"}\n\n` +
                            `La richiesta verrà rimossa e il Magazzino si aggiornerà.`
                          )) {
                            del.mutate(r.richiesta_id);
                          }
                        }}
                        disabled={del.isPending}
                        title="Elimina richiesta"
                        data-testid={`btn-del-${r.numero_richiesta}`}
                        className="p-1 text-rose-700 hover:bg-rose-100 rounded-sm disabled:opacity-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </div>

      {scannerFor && (
        <ScansioneInventario
          onClose={() => {
            setScannerFor(null);
            qc.invalidateQueries({ queryKey: ["richieste-materiali"] });
            qc.invalidateQueries({ queryKey: ["magazzino"] });
            qc.invalidateQueries({ queryKey: ["magazzino-movimenti"] });
            qc.invalidateQueries({ queryKey: ["bolle"] });
          }}
          cantieri={cantieri}
          richiestaIniziale={scannerFor}
        />
      )}

      {open && (
        <NuovaRichiestaModal
          onClose={() => setOpen(false)}
          onSave={(payload) => create.mutate(payload)}
          saving={create.isPending}
          cantieri={cantieri}
          fornitori={fornitori}
        />
      )}
      {accettaFor && (
        <AccettaRichiestaModal
          richiesta={accettaFor}
          onClose={() => setAccettaFor(null)}
          onDone={() => qc.invalidateQueries({ queryKey: ["richieste-materiali"] })}
        />
      )}
      {admin && prezziFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[85vh] overflow-auto">
            <div className="flex justify-between mb-4">
              <h2 className="text-lg font-bold">Prezzi ordine {prezziFor.numero_richiesta}</h2>
              <button onClick={() => setPrezziFor(null)}><X /></button>
            </div>
            <table className="w-full text-sm">
              <thead><tr><th>Materiale</th><th>Quantità</th><th>Prezzo unitario</th><th>Totale</th></tr></thead>
              <tbody>{(prezziFor.materiali || []).map((m, i) => (
                <tr key={i} className="border-t">
                  <td className="py-2">{m.descrizione}</td>
                  <td>{m.quantita} {m.unita_misura}</td>
                  <td>€ {Number(m.prezzo_unitario || 0).toFixed(2)}</td>
                  <td>€ {Number(m.totale_riga ?? ((m.quantita || 0) * (m.prezzo_unitario || 0))).toFixed(2)}</td>
                </tr>
              ))}</tbody>
            </table>
            <p className="text-right font-bold mt-4">Totale imponibile: € {Number(prezziFor.totale_valore || 0).toFixed(2)}</p>
          </div>
        </div>
      )}
      {storicoFor && (
        <StoricoRichiestaModal
          richiesta={storicoFor}
          onClose={() => setStoricoFor(null)}
        />
      )}
    </div>
  );
}

function StatoSelect({ richiesta, onChange }) {
  return (
    <select
      value={richiesta.stato}
      onChange={(e) => onChange(e.target.value)}
      data-testid={`stato-select-${richiesta.numero_richiesta}`}
      className="text-[11px] px-1.5 py-1 border border-slate-300 rounded-sm bg-white"
      title="Cambia stato manualmente"
    >
      {Object.entries(STATO_MAP).map(([k, v]) => (
        <option key={k} value={k}>{v.label}</option>
      ))}
    </select>
  );
}

const EMPTY_RIGA = {
  descrizione: "", quantita: 1, unita_misura: "pz", prezzo_unitario: 0,
  codice_articolo: "", note: "",
  // stato UI (non serializzato al backend richiesta)
  prendi_da_magazzino: false,
  suggerimento: null,
};
export { EMPTY_RIGA };

export function NuovaRichiestaModal({
  onClose, onSave, saving, cantieri, fornitori,
  defaultCantiereId, lockCantiere,
  defaultMateriali, defaultFornitoreNome, defaultFornitoreEmail,
  defaultFornitoreTelefono, defaultDataConsegna, defaultNote,
}) {
  const initialMateriali = Array.isArray(defaultMateriali) && defaultMateriali.length > 0
    ? defaultMateriali.map((m) => ({ ...EMPTY_RIGA, ...m }))
    : [{ ...EMPTY_RIGA }];
  const preFornitore = defaultFornitoreNome
    ? (fornitori || []).find((f) => (f.ragione_sociale || "").toLowerCase() === defaultFornitoreNome.toLowerCase())
    : null;
  const [form, setForm] = useState({
    cantiere_id: defaultCantiereId || "",
    fornitore_id: preFornitore?.fornitore_id || "",
    fornitore_nome: defaultFornitoreNome || preFornitore?.ragione_sociale || "",
    fornitore_email: defaultFornitoreEmail || preFornitore?.email || "",
    fornitore_telefono: defaultFornitoreTelefono || preFornitore?.telefono || "",
    fornitore_cc: "",
    data_consegna_richiesta: defaultDataConsegna || "",
    note: defaultNote || "",
    invia_email: true,
    materiali: initialMateriali,
    // v1.8.0 — ciclo richiesta fornitore completo
    oggetto: "",
    condizioni_pagamento: "30",
    aliquota_iva: 22,
  });

  // === PREFERITI RAPIDI (Template) ===
  const [templates, setTemplates] = useState([]);
  const [tplName, setTplName] = useState("");
  useEffect(() => {
    api.get("/richieste-materiali/template")
      .then((r) => setTemplates(r.data || []))
      .catch(() => {});
  }, []);

  const applyTemplate = async (tpl) => {
    if (!tpl) return;
    // Se ci sono già materiali compilati, chiedi conferma per sostituirli
    const hasContent = form.materiali.some((m) => m.descrizione || m.codice_articolo);
    if (hasContent && !window.confirm(`Applicare il preferito "${tpl.nome}"?\n\nLe righe correnti saranno sostituite.`)) {
      return;
    }
    setForm((f) => ({
      ...f,
      // Se il template ha un cantiere abituale e il form non è locked, precompilalo
      cantiere_id: (!lockCantiere && tpl.cantiere_id) ? tpl.cantiere_id : f.cantiere_id,
      fornitore_id: tpl.fornitore_id || f.fornitore_id,
      fornitore_nome: tpl.fornitore_nome || f.fornitore_nome,
      fornitore_email: tpl.fornitore_email || f.fornitore_email,
      fornitore_telefono: tpl.fornitore_telefono || f.fornitore_telefono,
      materiali: (tpl.materiali || []).map((m) => ({ ...EMPTY_RIGA, ...m })),
    }));
    try { await api.post(`/richieste-materiali/template/${tpl.template_id}/uso`); } catch {}
    const cantMsg = tpl.cantiere_nome ? ` · cantiere: ${tpl.cantiere_nome}` : "";
    toast.success(`Preferito "${tpl.nome}" applicato${cantMsg}`);
  };

  const salvaComePreferito = async () => {
    const nome = (tplName || form.fornitore_nome || "").trim();
    if (!nome) {
      toast.error("Dai un nome al preferito (o inserisci il fornitore)");
      return;
    }
    if (!form.materiali.some((m) => m.descrizione)) {
      toast.error("Aggiungi almeno un materiale prima di salvare");
      return;
    }
    try {
      const cantiereObj = (cantieri || []).find((c) => c.cantiere_id === form.cantiere_id);
      const { data } = await api.post("/richieste-materiali/template", {
        nome,
        fornitore_id: form.fornitore_id || null,
        fornitore_nome: form.fornitore_nome,
        fornitore_email: form.fornitore_email,
        fornitore_telefono: form.fornitore_telefono,
        cantiere_id: form.cantiere_id || null,
        cantiere_nome: cantiereObj?.nome || "",
        materiali: form.materiali
          .filter((m) => m.descrizione)
          .map((m) => ({
            descrizione: m.descrizione,
            quantita: Number(m.quantita) || 1,
            unita_misura: m.unita_misura || "pz",
            prezzo_unitario: Number(m.prezzo_unitario) || 0,
            codice_articolo: m.codice_articolo || "",
            note: m.note || "",
          })),
        note: form.note,
      });
      setTemplates((prev) => [data, ...prev]);
      setTplName("");
      toast.success(`⭐ Preferito "${data.nome}" salvato`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio preferito");
    }
  };

  // === BOZZA AUTOMATICA (localStorage 24h) ===
  // Le bozze sono per-cantiere (o "global" se non c'è cantiere): evita che una
  // bozza in ordine rapido cannibalizzi la bozza generale.
  const storageKey = `draft:richiesta-materiali:${defaultCantiereId || "global"}`;
  const { clearDraft, safeClose, backdropHandlers } = useFormDraft({
    storageKey,
    form,
    setForm,
    isDirty: (f) => Boolean(
      f.fornitore_nome ||
      f.note ||
      (f.materiali || []).some((r) => r.descrizione || r.codice_articolo)
    ),
    onClose,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setRiga = (idx, k, v) => setForm((f) => ({
    ...f,
    materiali: f.materiali.map((r, i) => i === idx ? { ...r, [k]: v } : r),
  }));
  const addRiga = () => setForm((f) => ({ ...f, materiali: [...f.materiali, { ...EMPTY_RIGA }] }));
  const delRiga = (idx) => setForm((f) => ({ ...f, materiali: f.materiali.filter((_, i) => i !== idx) }));

  // === SUGGERIMENTO PREZZI ===
  // Fetch debounced quando cambiano fornitore + (codice_articolo | descrizione).
  const suggerisciPrezzo = useCallback(async (idx, riga, fornitoreNome) => {
    if (!riga.codice_articolo && !riga.descrizione) return;
    if (!fornitoreNome && !riga.codice_articolo) return; // serve almeno fornitore o codice
    try {
      const { data } = await api.get("/richieste-materiali/suggerimento-prezzo", {
        params: {
          codice_articolo: riga.codice_articolo || "",
          descrizione: riga.descrizione || "",
          fornitore: fornitoreNome || "",
        },
      });
      if (!data) return;
      const suggerito = data.suggerito;
      setForm((f) => ({
        ...f,
        materiali: f.materiali.map((r, i) => {
          if (i !== idx) return r;
          // Non sovrascrive un prezzo che l'utente ha già toccato (>0) manualmente
          const shouldFill = !r.prezzo_unitario || Number(r.prezzo_unitario) === 0;
          return {
            ...r,
            prezzo_unitario: shouldFill && suggerito ? suggerito : r.prezzo_unitario,
            unita_misura: r.unita_misura === "pz" && data.magazzino?.unita_misura
              ? data.magazzino.unita_misura : r.unita_misura,
            suggerimento: {
              suggerito,
              magazzino: data.magazzino,
              ultime_bolle: data.ultimi_prezzi_bolle || [],
              fonte: data.ultimi_prezzi_bolle?.length ? "bolla" : (data.magazzino ? "magazzino" : null),
            },
          };
        }),
      }));
    } catch {
      // silenzioso — feature best-effort
    }
  }, []);

  // Debounce automatico su ogni riga: quando cambia codice/descrizione/fornitore
  useEffect(() => {
    const fornitoreNome = form.fornitore_nome;
    const timers = form.materiali.map((r, idx) => {
      if (!r.codice_articolo && !r.descrizione) return null;
      return setTimeout(() => suggerisciPrezzo(idx, r, fornitoreNome), 700);
    });
    return () => timers.forEach((t) => t && clearTimeout(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.fornitore_nome, JSON.stringify(form.materiali.map((r) => `${r.codice_articolo}|${r.descrizione}`))]);

  const onPickFornitore = (fid) => {
    if (!fid) {
      set("fornitore_id", "");
      return;
    }
    const f = fornitori.find((x) => x.fornitore_id === fid);
    if (!f) return;
    setForm((cur) => ({
      ...cur,
      fornitore_id: f.fornitore_id,
      fornitore_nome: f.ragione_sociale || cur.fornitore_nome,
      fornitore_email: f.email || cur.fornitore_email,
      fornitore_telefono: f.telefono || cur.fornitore_telefono,
    }));
  };

  const canSubmit = form.cantiere_id
    && form.fornitore_nome.trim()
    && form.materiali.some((r) => r.descrizione.trim() && Number(r.quantita) > 0);

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.error("Completa cantiere, fornitore e almeno un materiale con quantità");
      return;
    }
    const cc = form.fornitore_cc
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter((s) => s.includes("@"));

    // === ORDINE DA MAGAZZINO ===
    // Split righe: quelle marcate "prendi_da_magazzino" → scarico movimenti;
    // le altre continuano nella richiesta come ordine al fornitore.
    const dalMagazzino = form.materiali.filter(
      (r) => r.prendi_da_magazzino && r.descrizione.trim() && Number(r.quantita) > 0 && r.suggerimento?.magazzino?.materiale_id
    );
    const daOrdinare = form.materiali.filter(
      (r) => !r.prendi_da_magazzino && r.descrizione.trim() && Number(r.quantita) > 0
    );

    // 1) Movimenti di scarico dal magazzino (best-effort — errori mostrati come warning)
    for (const r of dalMagazzino) {
      try {
        await api.post("/magazzino/movimenti", {
          materiale_id: r.suggerimento.magazzino.materiale_id,
          tipo: "scarico",
          quantita: Number(r.quantita) || 0,
          cantiere_id: form.cantiere_id,
          note: `Da modulo Richiesta Materiali (fornitore ${form.fornitore_nome || "—"})`,
        });
        toast.success(`Magazzino: -${r.quantita} ${r.unita_misura} di ${r.descrizione}`);
      } catch (e) {
        toast.error(`Scarico ${r.descrizione}: ${e.response?.data?.detail || "errore"}`);
      }
    }

    // 2) Se non resta nulla da ordinare al fornitore, chiudo (magazzino only)
    if (daOrdinare.length === 0) {
      clearDraft();
      onClose();
      return;
    }

    // === SPLIT MULTI-FORNITORE ===
    // Righe con `fornitore_override` diverso dal fornitore principale creano
    // richieste separate (una per fornitore). Righe senza override vanno nel
    // fornitore principale della header.
    const gruppi = new Map();
    for (const r of daOrdinare) {
      const key = (r.fornitore_override || form.fornitore_nome || "").trim().toLowerCase();
      if (!gruppi.has(key)) gruppi.set(key, []);
      gruppi.get(key).push(r);
    }

    const buildPayload = (nomeForn, righe, fornitoreObj) => {
      const materiali = righe.map((r) => ({
        descrizione: r.descrizione.trim(),
        quantita: Number(r.quantita) || 0,
        unita_misura: r.unita_misura || "pz",
        prezzo_unitario: Number(r.prezzo_unitario) || 0,
        codice_articolo: r.codice_articolo || "",
        note: r.note || "",
      }));
      return {
        cantiere_id: form.cantiere_id,
        fornitore_id: fornitoreObj?.fornitore_id || (nomeForn === (form.fornitore_nome || "").trim().toLowerCase() ? form.fornitore_id : null),
        fornitore_nome: fornitoreObj?.ragione_sociale || nomeForn || form.fornitore_nome,
        fornitore_email: fornitoreObj?.email || (nomeForn === (form.fornitore_nome || "").trim().toLowerCase() ? form.fornitore_email : ""),
        fornitore_telefono: fornitoreObj?.telefono || (nomeForn === (form.fornitore_nome || "").trim().toLowerCase() ? form.fornitore_telefono : ""),
        fornitore_cc: cc,
        materiali,
        note: form.note || "",
        data_consegna_richiesta: form.data_consegna_richiesta || null,
        invia_email: !!form.invia_email && !!(fornitoreObj?.email || form.fornitore_email),
        oggetto: form.oggetto || "",
        condizioni_pagamento: form.condizioni_pagamento || "30",
        aliquota_iva: Number(form.aliquota_iva) || 22,
      };
    };

    if (gruppi.size === 1) {
      // Nessuno split — comportamento normale (una sola chiamata onSave)
      const key = [...gruppi.keys()][0];
      onSave(buildPayload(key, gruppi.get(key), null));
    } else {
      // Split: crea una richiesta per fornitore
      let ok = 0;
      const errors = [];
      for (const [nomeForn, righe] of gruppi.entries()) {
        const fornObj = fornitori.find((f) => (f.ragione_sociale || "").toLowerCase() === nomeForn);
        try {
          await api.post("/richieste-materiali", buildPayload(nomeForn, righe, fornObj));
          ok += 1;
        } catch (e) {
          errors.push(`${nomeForn}: ${e.response?.data?.detail || "errore"}`);
        }
      }
      toast.success(`Split multi-fornitore: ${ok} richieste create${errors.length ? " (" + errors.length + " errori)" : ""}`);
      if (errors.length) errors.forEach((m) => toast.error(m));
    }
    clearDraft();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" {...backdropHandlers}>
      <div
        className="bg-white w-full max-w-4xl border border-slate-200 max-h-[92vh] overflow-y-auto rounded-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="modal-nuova-richiesta"
      >
        <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="overline">Nuova richiesta</div>
            <h2 className="font-display font-bold text-xl">Ordine materiali al fornitore</h2>
            <div className="text-[10px] text-slate-500 mt-1 inline-flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-violet-500" />
              Prezzi suggeriti dalle ultime bolle · Bozza auto-salvata
            </div>
          </div>
          <button onClick={safeClose} data-testid="btn-close-modal">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Preferiti Rapidi */}
          {templates.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 flex items-center gap-2 flex-wrap" data-testid="rm-preferiti-panel">
              <span className="text-xs font-bold uppercase tracking-widest text-amber-800 whitespace-nowrap">
                ⭐ Preferiti Rapidi:
              </span>
              {templates.map((tpl) => (
                <button
                  key={tpl.template_id}
                  type="button"
                  onClick={() => applyTemplate(tpl)}
                  data-testid={`rm-preferito-${tpl.template_id}`}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold bg-white border border-amber-300 hover:bg-amber-100 text-amber-900 rounded-sm"
                  title={`${tpl.fornitore_nome} · ${(tpl.materiali || []).length} righe · usato ${tpl.uso_count || 0}×`}
                >
                  {tpl.nome}
                  <span className="text-[9px] opacity-60">({(tpl.materiali || []).length})</span>
                </button>
              ))}
            </div>
          )}

          {/* Cantiere + Fornitore */}
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-slate-600 block mb-1">Oggetto / Titolo richiesta (facoltativo)</label>
              <input
                value={form.oggetto}
                onChange={(e) => set("oggetto", e.target.value)}
                data-testid="rm-oggetto"
                placeholder="Es. ORDINE MATERIALI RISTRUTTURAZIONE APP. 4 · POSA PAVIMENTO PIANO TERRA"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Cantiere o Manutenzione *</label>
              <select
                value={form.cantiere_id}
                onChange={(e) => set("cantiere_id", e.target.value)}
                disabled={!!lockCantiere}
                data-testid="rm-cantiere"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm disabled:bg-slate-100 disabled:cursor-not-allowed"
              >
                <option value="">— Seleziona cantiere o manutenzione —</option>
                <optgroup label="🏗️ Cantieri">
                  {cantieri.filter((c) => c.tipo !== "manutenzione").map((c) => (
                    <option key={c.cantiere_id} value={c.cantiere_id}>{c.nome}</option>
                  ))}
                </optgroup>
                <optgroup label="🔧 Manutenzioni">
                  {cantieri.filter((c) => c.tipo === "manutenzione").map((c) => (
                    <option key={c.cantiere_id} value={c.cantiere_id}>{c.nome}</option>
                  ))}
                </optgroup>
              </select>
              {lockCantiere && (
                <div className="text-[11px] text-slate-500 mt-1">
                  Cantiere fissato automaticamente da questa pagina.
                </div>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Consegna richiesta entro</label>
              <input
                type="date"
                value={form.data_consegna_richiesta}
                onChange={(e) => set("data_consegna_richiesta", e.target.value)}
                data-testid="rm-consegna"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Fornitore da anagrafica</label>
              <select
                value={form.fornitore_id}
                onChange={(e) => onPickFornitore(e.target.value)}
                data-testid="rm-fornitore-select"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
              >
                <option value="">— Nuovo / non presente —</option>
                {fornitori.map((f) => (
                  <option key={f.fornitore_id} value={f.fornitore_id}>{f.ragione_sociale}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Nome fornitore *</label>
              <input
                value={form.fornitore_nome}
                onChange={(e) => set("fornitore_nome", e.target.value)}
                data-testid="rm-fornitore-nome"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Email fornitore</label>
              <input
                type="email"
                value={form.fornitore_email}
                onChange={(e) => set("fornitore_email", e.target.value)}
                data-testid="rm-fornitore-email"
                placeholder="ordini@fornitore.it"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
              />
              {!form.fornitore_email && (
                <div className="text-[11px] text-amber-700 mt-1">Senza email l'invio automatico è saltato. Potrai comunque scaricare il PDF o inviarlo via WhatsApp.</div>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Telefono / WhatsApp fornitore</label>
              <input
                type="tel"
                value={form.fornitore_telefono}
                onChange={(e) => set("fornitore_telefono", e.target.value)}
                data-testid="rm-fornitore-telefono"
                placeholder="+39 333 1234567"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
              />
              <div className="text-[11px] text-emerald-700 mt-1 font-semibold">
                📱 Se compilato, WhatsApp si aprirà automaticamente dopo la creazione.
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">CC (email separate da virgola)</label>
              <input
                value={form.fornitore_cc}
                onChange={(e) => set("fornitore_cc", e.target.value)}
                data-testid="rm-fornitore-cc"
                placeholder="responsabile@azienda.it, magazzino@azienda.it"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
              />
            </div>
          </div>

          {/* Materiali */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="overline">Materiali richiesti</div>
              <button
                onClick={addRiga}
                data-testid="btn-add-riga"
                className="text-xs inline-flex items-center gap-1 px-2 py-1 border border-slate-300 hover:bg-slate-50 rounded-sm font-semibold"
              >
                <Plus className="w-3 h-3" /> Aggiungi riga
              </button>
            </div>
            <div className="overflow-x-auto border border-slate-200 rounded-md">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-[10px] uppercase tracking-widest text-slate-500">
                    <th className="text-left p-2">Descrizione</th>
                    <th className="text-left p-2 w-24">Cod. articolo</th>
                    <th className="text-right p-2 w-20">Q.tà</th>
                    <th className="text-left p-2 w-16">U.M.</th>
                    <th className="text-right p-2 w-24">P.U. €</th>
                    <th className="text-right p-2 w-24">Totale €</th>
                    <th className="text-left p-2">Note riga</th>
                    <th className="p-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.materiali.map((r, idx) => {
                    const totRiga = (Number(r.quantita) || 0) * (Number(r.prezzo_unitario) || 0);
                    const mag = r.suggerimento?.magazzino;
                    const disponibileInMag = mag && mag.quantita_disponibile >= (Number(r.quantita) || 0);
                    const suggerito = r.suggerimento?.suggerito;
                    return (
                    <React.Fragment key={idx}>
                    <tr className="border-t border-slate-100">
                      <td className="p-1">
                        <input
                          value={r.descrizione}
                          onChange={(e) => setRiga(idx, "descrizione", e.target.value)}
                          data-testid={`rm-riga-desc-${idx}`}
                          placeholder="Es. Sacchi cemento 25kg"
                          className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm"
                        />
                      </td>
                      <td className="p-1">
                        <input
                          value={r.codice_articolo}
                          onChange={(e) => setRiga(idx, "codice_articolo", e.target.value)}
                          className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-xs font-mono"
                        />
                      </td>
                      <td className="p-1">
                        <input
                          type="number" min="0" step="0.01"
                          value={r.quantita}
                          onChange={(e) => setRiga(idx, "quantita", e.target.value)}
                          data-testid={`rm-riga-qta-${idx}`}
                          className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm text-right font-mono"
                        />
                      </td>
                      <td className="p-1">
                        <select
                          value={r.unita_misura}
                          onChange={(e) => setRiga(idx, "unita_misura", e.target.value)}
                          className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm"
                        >
                          {UM_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </td>
                      <td className="p-1">
                        <input
                          type="number" min="0" step="0.01"
                          value={r.prezzo_unitario}
                          onChange={(e) => setRiga(idx, "prezzo_unitario", e.target.value)}
                          data-testid={`rm-riga-pu-${idx}`}
                          placeholder={suggerito ? suggerito.toFixed(2) : "0.00"}
                          title={suggerito ? `Suggerito: € ${suggerito.toFixed(2)} (da ${r.suggerimento.fonte === "bolla" ? "ultima bolla" : "listino magazzino"})` : ""}
                          className={`w-full px-2 py-1.5 border rounded-sm text-sm text-right font-mono ${suggerito && (!r.prezzo_unitario || Number(r.prezzo_unitario) === 0) ? "border-violet-300 bg-violet-50/30" : "border-slate-200"}`}
                        />
                      </td>
                      <td className="p-1 text-right font-mono text-sm font-bold text-orange-700" data-testid={`rm-riga-tot-${idx}`}>
                        {totRiga > 0 ? totRiga.toFixed(2) : "—"}
                      </td>
                      <td className="p-1">
                        <input
                          value={r.note}
                          onChange={(e) => setRiga(idx, "note", e.target.value)}
                          className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-sm"
                        />
                      </td>
                      <td className="p-1 text-center">
                        {form.materiali.length > 1 && (
                          <button onClick={() => delRiga(idx)} className="p-1 text-rose-700 hover:bg-rose-50 rounded-sm" title="Rimuovi riga">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                    {(r.suggerimento || mag) && (
                      <tr className="bg-slate-50/70">
                        <td colSpan={8} className="px-2 py-1.5">
                          <div className="flex items-center flex-wrap gap-3 text-[11px]">
                            {suggerito != null && (
                              <div className="inline-flex items-center gap-1 text-violet-800">
                                <Sparkles className="w-3 h-3" />
                                <span>
                                  Suggerito: <b>€ {suggerito.toFixed(2)}</b> {r.suggerimento.fonte === "bolla" ? "(ultima bolla)" : "(listino magazzino)"}
                                </span>
                                {r.suggerimento.ultime_bolle?.[0] && (
                                  <span className="text-slate-500 ml-1">
                                    · {r.suggerimento.ultime_bolle[0].data} · {r.suggerimento.ultime_bolle[0].fornitore}
                                  </span>
                                )}
                              </div>
                            )}
                            {mag && (
                              <label
                                className={`inline-flex items-center gap-1.5 cursor-pointer px-2 py-1 rounded-sm border ${
                                  r.prendi_da_magazzino
                                    ? "bg-emerald-50 border-emerald-400 text-emerald-800"
                                    : "bg-white border-slate-300 hover:bg-slate-50 text-slate-700"
                                } ${!disponibileInMag ? "opacity-60" : ""}`}
                                data-testid={`rm-magazzino-toggle-${idx}`}
                                title={disponibileInMag
                                  ? "Prendi la quantità dal magazzino invece di ordinarla al fornitore"
                                  : `Solo ${mag.quantita_disponibile} ${mag.unita_misura} disponibili`}
                              >
                                <input
                                  type="checkbox"
                                  checked={!!r.prendi_da_magazzino}
                                  disabled={!disponibileInMag}
                                  onChange={(e) => setRiga(idx, "prendi_da_magazzino", e.target.checked)}
                                  className="w-3 h-3"
                                />
                                <Warehouse className="w-3 h-3" />
                                <span className="font-semibold">Prendi dal magazzino</span>
                                <span className="text-[10px] font-mono">
                                  · disponibili: <b>{mag.quantita_disponibile} {mag.unita_misura}</b>
                                </span>
                                {mag.quantita_disponibile <= mag.quantita_minima && mag.quantita_minima > 0 && (
                                  <span className="text-[10px] text-amber-700 font-bold">⚠ sotto scorta</span>
                                )}
                              </label>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                  <tr className="font-bold">
                    <td colSpan={2} className="p-2 text-right text-slate-600 uppercase text-[10px] tracking-widest">Totali ordine</td>
                    <td className="p-2 text-right font-mono text-slate-900" data-testid="rm-tot-colli">
                      {form.materiali.reduce((s, r) => s + (Number(r.quantita) || 0), 0).toFixed(2)}
                    </td>
                    <td className="p-2 text-[10px] text-slate-500 uppercase">colli/pz totali</td>
                    <td colSpan={1}></td>
                    <td className="p-2 text-right font-mono text-orange-700 text-sm" data-testid="rm-tot-valore">
                      € {form.materiali.reduce((s, r) => s + (Number(r.quantita) || 0) * (Number(r.prezzo_unitario) || 0), 0).toFixed(2)}
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Note generali */}
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Note generali (facoltative)</label>
            <textarea
              value={form.note}
              onChange={(e) => set("note", e.target.value)}
              rows={3}
              placeholder="Consegna alla mattina, chiedere di Mario, portare cavalletti, ecc."
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
            />
          </div>

          {/* Condizioni di pagamento & IVA */}
          <div className="grid md:grid-cols-3 gap-3 text-sm bg-emerald-50/50 border border-emerald-200 rounded-md p-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-emerald-800 block mb-1">
                💰 Condizioni di pagamento
              </label>
              <select
                value={form.condizioni_pagamento}
                onChange={(e) => set("condizioni_pagamento", e.target.value)}
                data-testid="rm-condizioni"
                className="w-full px-3 py-2 border border-emerald-300 rounded-sm text-sm bg-white"
              >
                {COND_PAGAMENTO.map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
              <div className="text-[10px] text-emerald-800 mt-1">
                Alla ACCETTAZIONE le rate vengono generate automaticamente in Contabilità.
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-emerald-800 block mb-1">
                Aliquota IVA %
              </label>
              <input
                type="number" min="0" max="100" step="1"
                value={form.aliquota_iva}
                onChange={(e) => set("aliquota_iva", e.target.value)}
                data-testid="rm-iva"
                className="w-full px-3 py-2 border border-emerald-300 rounded-sm text-sm bg-white font-mono"
              />
            </div>
            <div className="flex flex-col justify-end text-[11px] text-emerald-900">
              {(() => {
                const tot = form.materiali.reduce((s, r) => s + (Number(r.quantita) || 0) * (Number(r.prezzo_unitario) || 0), 0);
                const iva = tot * (Number(form.aliquota_iva) || 0) / 100;
                return (
                  <>
                    <div>Imponibile: <b>€ {tot.toFixed(2)}</b></div>
                    <div>IVA: <b>€ {iva.toFixed(2)}</b></div>
                    <div className="font-bold text-emerald-800">Totale: € {(tot + iva).toFixed(2)}</div>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Toggle invio email + Salva Preferito */}
          <div className="flex flex-col md:flex-row gap-3">
            <label className="flex items-start gap-2 text-sm cursor-pointer p-3 border border-slate-200 rounded-md bg-slate-50 flex-1">
              <input
                type="checkbox"
                checked={form.invia_email}
                onChange={(e) => set("invia_email", e.target.checked)}
                data-testid="rm-invia-email"
                className="mt-1"
              />
              <div>
                <div className="font-semibold">Invia email al fornitore con PDF allegato</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Se disattivato, la richiesta viene creata ma non parte alcuna email.
                </div>
              </div>
            </label>
            <div className="p-3 border border-amber-200 bg-amber-50 rounded-md md:w-80">
              <div className="text-xs font-bold uppercase tracking-widest text-amber-800 mb-1">
                ⭐ Salva come preferito
              </div>
              <div className="flex gap-1">
                <input
                  value={tplName}
                  onChange={(e) => setTplName(e.target.value)}
                  data-testid="rm-tpl-name"
                  placeholder={form.fornitore_nome || "Nome del preferito"}
                  className="flex-1 px-2 py-1.5 border border-amber-300 rounded-sm text-xs"
                />
                <button
                  type="button"
                  onClick={salvaComePreferito}
                  data-testid="rm-tpl-save"
                  className="px-2 py-1.5 text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded-sm"
                >
                  Salva
                </button>
              </div>
              <p className="text-[10px] text-amber-800 mt-1">
                Riusa fornitore + materiali con un click.
              </p>
            </div>
          </div>
        </div>

        <div className="p-5 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50"
          >
            Annulla
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !canSubmit}
            data-testid="btn-submit-richiesta"
            className="px-4 py-2 text-sm bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-semibold rounded-sm inline-flex items-center gap-2"
          >
            {saving ? "Invio…" : <><Send className="w-4 h-4" /> Crea e invia</>}
          </button>
        </div>
      </div>
    </div>
  );
}
